/**
 * Gear Core - pure logic for the Gear tab (phase-1 goal tracker).
 *
 * Computes the 5-skill x 3-technique tech gear matrix, per-item crafting
 * status, the suggested focus (the simulator's focused-crafting strategy,
 * simplified), artifact statuses, and material sourcing hints. No DOM.
 */

import * as config from '../constants.js';
import { gameState } from './gameState-core.js';
import { findItem, findArtifact, getAllArtifacts, getSkillFaction } from './util.js';
// From recipes-core, NOT crafting.js: crafting.js carries the crafting UI, and
// importing it here made this -core module unloadable in Node.
import { getRecipeById } from './recipes-core.js';

const capitalize = s => s.charAt(0).toUpperCase() + s.slice(1);

/**
 * The faction's tech gear arranged as the 5x3 matrix:
 * [{ skillId, skill, techniques: [{ technique, item }] }] - one entry per
 * faction skill, one technique row per skill, each carrying its gear item.
 */
export function getGearMatrix(faction) {
    const skills = config.factionSkills[faction]?.skills || {};
    const techniques = config.skillTechniques[faction] || [];
    const gearItems = config[`itemsEpicDevice${capitalize(faction)}`] || [];

    return Object.entries(skills).map(([skillId, skill]) => ({
        skillId,
        skill,
        techniques: techniques
            .filter(t => t.skill === skillId)
            .map(technique => ({
                technique,
                item: gearItems.find(i => i.techniqueId === technique.id) || null
            }))
            .filter(entry => entry.item)
    }));
}

/**
 * Crafting status for one gear/artifact target.
 * owned counts inventory AND equipped technique gear; progress is the
 * weighted have/need share across direct inputs; missing lists shortfalls.
 */
export function getGearStatus(itemId, character = gameState.character) {
    const inventory = character?.inventory || {};
    const equipped = Object.values(character?.equippedTechniqueGear || {});
    const owned = (inventory[itemId] || 0) > 0 ||
        equipped.some(g => g === itemId || g?.id === itemId);

    const recipe = getRecipeById(itemId);
    if (!recipe) {
        return { itemId, owned, craftable: false, progress: owned ? 1 : 0, missing: [], missingUnits: 0, recipe: null };
    }

    let haveSum = 0, needSum = 0, missingUnits = 0;
    const missing = [];
    for (const input of recipe.inputs) {
        const have = inventory[input.id] || 0;
        haveSum += Math.min(have, input.qty);
        needSum += input.qty;
        if (have < input.qty) {
            missing.push({ id: input.id, have, need: input.qty, short: input.qty - have });
            missingUnits += input.qty - have;
        }
    }

    return {
        itemId,
        owned,
        craftable: !owned && missing.length === 0,
        progress: needSum > 0 ? haveSum / needSum : 0,
        missing,
        missingUnits,
        recipe
    };
}

/**
 * Suggested focus: the un-owned gear piece with the fewest missing input
 * units (ties broken by higher progress). Null once the set is complete.
 */
export function getSuggestedFocus(faction, character = gameState.character) {
    let best = null;
    for (const column of getGearMatrix(faction)) {
        for (const { item } of column.techniques) {
            const status = getGearStatus(item.id, character);
            if (status.owned) continue;
            if (!best ||
                status.missingUnits < best.status.missingUnits ||
                (status.missingUnits === best.status.missingUnits && status.progress > best.status.progress)) {
                best = { item, status };
            }
        }
    }
    return best;
}

/**
 * The artifact a player should chase next — the artifact half of the Gear Focus
 * panel, which until now only knew about skill gear.
 *
 * Same rule as getSuggestedFocus: fewest missing units, tie-broken by progress.
 * Two differences that matter, both from artifacts being blueprint-gated:
 *
 *   - An artifact whose blueprint is not held cannot be worked toward by
 *     crafting at all, only by raiding Tier 3 until one drops. Those are ranked
 *     LAST, so the panel points at something actionable when anything is.
 *   - Already-crafted artifacts are skipped: a second copy earns no marks
 *     (lifetime.artifactsCrafted counts distinct designs).
 *
 * @returns {{artifact: object, status: object, hasBlueprint: boolean}|null}
 */
export function getSuggestedArtifactFocus(character = gameState.character) {
    let best = null;
    for (const entry of getArtifactStatuses(character)) {
        if (entry.copiesOwned > 0 || entry.status.owned) continue;
        if (!best) { best = entry; continue; }
        // Blueprint-in-hand always outranks blueprint-missing.
        if (entry.hasBlueprint !== best.hasBlueprint) {
            if (entry.hasBlueprint) best = entry;
            continue;
        }
        if (entry.status.missingUnits < best.status.missingUnits
            || (entry.status.missingUnits === best.status.missingUnits
                && entry.status.progress > best.status.progress)) {
            best = entry;
        }
    }
    return best;
}

/**
 * Artifact statuses for the character's faction (+ neutral): blueprint
 * ownership, materials progress, and how many copies were ever crafted.
 */
export function getArtifactStatuses(character = gameState.character) {
    const faction = character?.faction;
    const inventory = character?.inventory || {};
    const results = [];

    for (const artifact of Object.values(getAllArtifacts())) {
        if (artifact.faction !== 'neutral' && artifact.faction !== faction) continue;
        const status = getGearStatus(artifact.id, character);
        // Blueprints bank under their slug item id (the old name-keyed
        // lookup never matched dropped blueprints)
        results.push({
            artifact,
            status,
            hasBlueprint: (inventory[`${artifact.id}-blueprint`] || 0) > 0,
            copiesOwned: (character?.artifacts || []).filter(a => a.id === artifact.id).length
        });
    }
    return results;
}

/**
 * Sourcing hint for a material: can it be crafted, and where does it drop?
 * Drop sourcing follows the engine's faction-scoped material pools
 * (common/rare materials drop in rooms of their faction); regions are
 * ranked by how many gate/hazard rooms belong to that faction. Uncommon
 * intermediates are craft-only under the current economy.
 */
const sourceCache = new Map();
export function getMaterialSources(itemId) {
    if (sourceCache.has(itemId)) return sourceCache.get(itemId);

    const item = findItem('id', itemId);
    const craftable = !!getRecipeById(itemId);
    const result = { craftable, regions: [], skills: [] };

    if (item && item.type === 'material' && item.faction &&
        (item.rarity === 'common' || item.rarity === 'rare')) {
        const skillSet = new Set();
        const regions = Object.entries(config.raidRegions).map(([key, region]) => {
            let rooms = 0;
            for (const room of [...(region.gates || []), ...(region.hazards || [])]) {
                if (getSkillFaction(room.primary) === item.faction) {
                    rooms++;
                    skillSet.add(room.primary);
                }
                else if (item.faction == "neutral")
                    rooms++;
            }
            return { key, name: region.name, rooms };
        }).filter(r => r.rooms > 0).sort((a, b) => b.rooms - a.rooms);

        result.regions = regions;
        result.skills = [...skillSet];
    }

    sourceCache.set(itemId, result);
    return result;
}

/**
 * One-line sourcing text for a missing input, used by the focus card:
 * craft path (with readiness) and/or the best drop region.
 */
export function describeMaterialSource(missingInput, character = gameState.character) {
    const parts = [];
    const sources = getMaterialSources(missingInput.id);

    if (sources.craftable) {
        const subStatus = getGearStatus(missingInput.id, character);
        /*parts.push(subStatus.missing.length === 0
            ? `craft from materials you have (${missingInput.short} needed)`
            : `craftable — missing ${subStatus.missing.map(m => findItem('id', m.id)?.name || m.id).join(', ')}`);*/
        parts.push("craftable");
    }
    if (sources.regions.length > 0) {
        const skillNames = sources.skills
            .map(s => {
                const faction = getSkillFaction(s);
                return config.factionSkills[faction]?.skills[s]?.name;
            })
            .filter(Boolean).slice(0, 2).join(' / ');
        parts.push((skillNames == "" ? "" : `best found in ${sources.regions[0].name}`));
    }
    return parts.join(' or ');
}

// How a player ranks utility-artifact effects. All 11 effects are
// mechanically live (mostly via eventSystem-core), but they are not
// commensurable - "prevent a knockout" and "+5% credits" cannot be compared
// numerically - so the ordering is a stated player judgement, tuned to what the
// run is actually bottlenecked on:
//
//   1. Survival. Total team wipe is the ONLY way a raid fails, and Tier 3 still
//      wipes ~23% of the time, so anything that stops a knockout is worth most.
//   2. Blueprints. They gate artifact unlocks, the largest marks source
//      (25/unlock + 8/craft vs 4/gear).
//   3. Material throughput. Feeds the 15-piece gear set that drives the early
//      marks curve.
//   4. Economy. Credits are tight (~95% of income absorbed) but the float
//      target passes, so this is real but not binding.
//   5. Niche / situational.
//
// Higher wins. Ties break on the effect's own magnitude.
export const UTILITY_EFFECT_PRIORITY = {
  'knockout-prevention': 100,     // stops the only loss condition outright
  'hazard-damage-reduction': 90,
  'blueprint-find-bonus': 80,     // gates the artifact era
  'loot-quantity-bonus': 70,
  'gate-material-bonus': 65,
  'raid-energy-reduction': 50,    // more raids per day = more of everything
  'currency-bonus': 40,
  'contractor-payout-bonus': 35,
  'faction-synergy-bonus': 30,
  'support-skill-bonus': 25,
  'relic-quality-bonus': 20,
};

/** Score a utility artifact by its best-priority effect, magnitude as tiebreak. */
export function scoreUtilityArtifact(artifact) {
  let best = -1;
  for (const [effectId, magnitude] of Object.entries(artifact?.effects || {})) {
    const p = UTILITY_EFFECT_PRIORITY[effectId];
    if (p == null) continue;
    const score = p * 1000 + (Number(magnitude) || 0);
    if (score > best) best = score;
  }
  return best;
}

/** Artifacts the character owns (crafted into inventory). */
function ownedArtifacts(char) {
  return Object.entries(char.inventory || {})
    .filter(([, qty]) => qty > 0)
    .map(([id]) => findArtifact('id', id))
    .filter(Boolean);
}

/**
 * Re-pick both artifact slots from what the character owns.
 *
 * Replaces equipping at craft time only, which meant the FIRST artifact crafted
 * for a slot held it for the character's whole life - the utility slot was never
 * compared at all, and the skill slot was never revisited as skills levelled.
 * A player re-evaluates; this does too, once per raid.
 */
export function reequipArtifacts(char) {
  const owned = ownedArtifacts(char);
  if (owned.length === 0) return;
  if (!char.equippedArtifacts) char.equippedArtifacts = { skillSlot: null, utilitySlot: null };

  // Skill slot: back the character's strongest skill.
  const skillCandidates = owned.filter(a => a.slot === 'skillSlot');
  if (skillCandidates.length) {
    char.equippedArtifacts.skillSlot = skillCandidates.reduce((best, a) =>
      (char.skills?.[a.skill] || 0) > (char.skills?.[best.skill] || 0) ? a : best);
  }

  // Utility slot: highest-priority effect available.
  const utilityCandidates = owned.filter(a => a.slot === 'utilitySlot');
  if (utilityCandidates.length) {
    char.equippedArtifacts.utilitySlot = utilityCandidates.reduce((best, a) =>
      scoreUtilityArtifact(a) > scoreUtilityArtifact(best) ? a : best);
  }
}
